import React, { useState, useRef } from 'react'
import { IconButton, Badge, Popover, Typography, Box, CircularProgress } from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import DownloadPanel, { DownloadItem } from './DownloadPanel'

const StyledIconButton = styled(IconButton)(() => ({
  border: '1px solid #5F55D1',
  borderRadius: '3px',
  color: '#5F55D1',
  background: '#E3FCEE',
  boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.15)',
  '&:hover': {
    background: '#a1eabc',
  },
}))

interface DownloadIndicatorProps {
  downloads: DownloadItem[]
  onDownload: (item: DownloadItem, fileName: string) => void
  onCancel: (itemId: string) => void
  onDismiss: (itemId: string) => void
  onFileNameChange: (itemId: string, fileName: string) => void
  autoDownload: boolean
  onAutoDownloadToggle: (enabled: boolean) => void
}

const DownloadIndicator: React.FC<DownloadIndicatorProps> = ({
  downloads,
  onDownload,
  onCancel,
  onDismiss,
  onFileNameChange,
  autoDownload,
  onAutoDownloadToggle,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Ensure we have a valid anchor element
    if (event.currentTarget) {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Get download status for indicator
  const getIndicatorStatus = () => {
    if (downloads.length === 0) return null

    const hasActiveDownloads = downloads.some(
      (d) => d.status === 'preparing' || d.status === 'downloading'
    )
    const hasCompletedDownloads = downloads.some((d) => d.status === 'completed')
    const hasErrorDownloads = downloads.some((d) => d.status === 'error')

    if (hasErrorDownloads) return 'error'
    if (hasActiveDownloads) return 'active'
    if (hasCompletedDownloads) return 'completed'
    return null
  }

  const getIndicatorIcon = () => {
    const status = getIndicatorStatus()

    switch (status) {
      case 'active':
        return <CircularProgress size={20} />
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />
      case 'error':
        return <DownloadIcon sx={{ color: 'error.main' }} />
      default:
        return <DownloadIcon />
    }
  }

  const getIndicatorColor = () => {
    const status = getIndicatorStatus()

    switch (status) {
      case 'active':
        return 'primary'
      case 'completed':
        return 'success'
      case 'error':
        return 'error'
      default:
        return 'default'
    }
  }

  const getBadgeContent = () => {
    return downloads.length
  }

  if (downloads.length === 0) return null

  return (
    <>
      <StyledIconButton
        onClick={handleClick}
        size="small"
        aria-label="Download status"
        title="View downloads"
      >
        <Badge
          badgeContent={getBadgeContent()}
          color={
            getIndicatorColor() as
              | 'primary'
              | 'secondary'
              | 'error'
              | 'info'
              | 'success'
              | 'warning'
          }
          max={99}
        >
          {getIndicatorIcon()}
        </Badge>
      </StyledIconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          ref: popoverRef,
          sx: {
            p: 2,
            mt: 1,
            minWidth: 400,
            maxWidth: 600,
            maxHeight: 500,
            overflow: 'auto',
          },
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          <Typography variant="h6" component="h2">
            Downloads
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="Close downloads">
            <CloseIcon />
          </IconButton>
        </Box>

        <DownloadPanel
          downloads={downloads}
          onDownload={onDownload}
          onCancel={onCancel}
          onDismiss={onDismiss}
          onFileNameChange={onFileNameChange}
          autoDownload={autoDownload}
          onAutoDownloadToggle={onAutoDownloadToggle}
          compact
        />
      </Popover>
    </>
  )
}

export default DownloadIndicator
